"use client";
import { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useRef } from "react";
import { DEFAULT_SCRIPT_ID, Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";

export type CaptchaHandle = {
  /** Runs a fresh challenge. Rejects on any widget failure instead of waiting it out. */
  getToken(): Promise<string>;
  reset(): void;
};

// An invisible challenge normally solves in about two seconds. The same limit
// covers waiting for the widget to load when a visitor submits very quickly.
const TOKEN_TIMEOUT_MS = 15_000;

type Pending = { resolve(token: string): void; reject(error: Error): void };
type WidgetState = { status: "loading" } | { status: "ready" } | { status: "failed"; reason: string };

// Cloudflare Turnstile in execute mode: the challenge runs only when a form is
// submitted, so every submission gets a fresh single-use token. The widget type
// (Invisible) is configured on the key in Cloudflare, not here.
export const Captcha = forwardRef<CaptchaHandle>(function Captcha(_, ref) {
  const widget = useRef<TurnstileInstance>();
  // "ready" is set by onWidgetLoad, which fires only once the script has loaded
  // and the widget rendered; before that the library's execute() does nothing.
  const state = useRef<WidgetState>({ status: "loading" });
  const pending = useRef<Pending>();
  const id = useId();

  const fail = useCallback((reason: string) => {
    // An invisible widget shows nothing when misconfigured (e.g. hostname not
    // allowed), so surface Cloudflare's reason for whoever debugs it.
    console.error(`Turnstile: ${reason}`);
    pending.current?.reject(new Error(reason));
  }, []);
  const failWidget = useCallback((reason: string) => {
    state.current = { status: "failed", reason };
    fail(reason);
  }, [fail]);

  useImperativeHandle(ref, () => ({
    getToken() {
      const current = state.current;
      if (current.status === "failed") {
        return Promise.reject(new Error(current.reason));
      }
      return new Promise<string>((resolve, reject) => {
        const settle = () => {
          clearTimeout(timer);
          if (pending.current === request) pending.current = undefined;
        };
        const request: Pending = {
          resolve: (token) => { settle(); resolve(token); },
          reject: (error) => { settle(); reject(error); },
        };
        const timer = setTimeout(() => {
          console.error("Turnstile: timed out");
          request.reject(new Error("timed out"));
        }, TOKEN_TIMEOUT_MS);
        pending.current?.reject(new Error("superseded by a newer request"));
        pending.current = request;
        // Still loading: onWidgetLoad runs the challenge once the widget exists.
        if (current.status === "ready") widget.current?.execute();
      });
    },
    reset() {
      widget.current?.reset();
    },
  }), []);

  // The library's scriptOptions.onError also deletes its onload callback, which
  // makes Turnstile log an error on every page, so watch the script tag directly.
  // Turnstile's own effect (a child effect, so it runs first) has inserted it.
  // The tag outlives client-side navigation and never fires again, so a failure
  // is marked on it for widgets mounted later.
  useEffect(() => {
    const script = document.getElementById(DEFAULT_SCRIPT_ID);
    if (!script) return;
    if (script.dataset.failed) {
      failWidget("script failed to load");
      return;
    }
    const onScriptError = () => {
      script.dataset.failed = "true";
      failWidget("script failed to load");
    };
    script.addEventListener("error", onScriptError);
    return () => script.removeEventListener("error", onScriptError);
  }, [failWidget]);

  return (
    <Turnstile
      ref={widget}
      id={id}
      siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY!}
      options={{ execution: "execute", appearance: "execute" }}
      onWidgetLoad={() => {
        state.current = { status: "ready" };
        if (pending.current) widget.current?.execute();
      }}
      onSuccess={(token) => pending.current?.resolve(token)}
      // Execute mode runs no challenge before a submit, so an error before the
      // widget loaded means it could not render at all (e.g. a missing site key).
      onError={(code) => state.current.status === "loading" ? failWidget(`error ${code}`) : fail(`error ${code}`)}
      onExpire={() => fail("token expired")}
      onTimeout={() => fail("challenge timed out")}
      onUnsupported={() => fail("browser not supported")}
    />
  );
});
